import { get } from 'lodash'
import { runInAction } from 'mobx'
import rootStore from '../..'
import { wrapWithLoading } from '../../../config/general-actions'
import { responseHasError } from '../../users/actions'
import { PLAN_FOR_DELETION_NR_OF_DAYS, warnPt } from '../../../config'
import { requireBudgetById } from '../../budget/actions'
import { deleteLstItem } from '../../../api/utils/api-delete'
import dayjs from 'dayjs'
import { getCompactLst } from '../../../api/utils/api-compact'
const _storeKey = 'uiLstBudget'
/**
 * @function calculateBudgetOverview
 * @description Calculates the total "IN" and "OUT" budgets from the UI list store based on the selected currency.
 * It iterates through the stored budget IDs and uses `.convertCurrency()` to normalize values before summing.
 *
 * @param {string} currency - Currency code to which values should be converted (default is 'usd').
 * @returns {{ budget_in: number, budget_out: number }} An object containing the total incoming and outgoing amounts.
 */
export const calculateBudgetOverview = (currency = 'usd') => {
	let budget_in = 0
	let budget_out = 0

	rootStore[_storeKey].ids.map((id) => {
		const budget = requireBudgetById(id)
		if (budget.budget_type === 'in') {
			budget_in += budget.convertCurrency(currency)
		} else {
			budget_out += budget.convertCurrency(currency)
		}
	})

	return {
		budget_in,
		budget_out
	}
}

/**
 * @async
 * @function actLoadBudgetList
 * @description Asynchronously loads list of budgets from the server and updates the UI list store.
 * @returns {Promise<void>}
 */
export const actLoadBudgetList = wrapWithLoading(
	async () => {
		const { filter } = rootStore[_storeKey]
		const params = {
			pg: rootStore[_storeKey].pg,
			rpg: rootStore[_storeKey].rpg,
			del: rootStore[_storeKey].del,
			search: rootStore[_storeKey].search,
			assigned_user_id: filter?.assigned_user_id || '',
			...(filter?.period_start
				? { period_start: filter.period_start }
				: {}),
			...(filter?.period_end ? { period_end: filter.period_end } : {})
		}

		const _response = await getCompactLst('budgets', params)
		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_actSync(
					get(_response, ['itms'], []),
					get(_response, ['nr'], 0)
				)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Loading Budgets' }
)

/**
 * @function _actSync
 * @description Syncs the server items with the store items.
 * @param {Array} srvItms - Server items.
 * @param {number} nr - Number of items.
 */
const _actSync = (srvItms, nr) => {
	warnPt(srvItms, nr)
	runInAction(() => {
		for (const _srvItm of srvItms) {
			const _storeItm = requireBudgetById(_srvItm.budget_id)
			_storeItm.updateFromServer(_srvItm)
		}

		rootStore[_storeKey].setIds(srvItms.map((it) => it.budget_id))
		rootStore[_storeKey].setNr(nr)
	})
}

/**
 * @async
 * @function actUndoOrDeleteBudget
 * @description Either marks a budget for deletion (soft delete) or undoes a prior deletion mark.
 * Optionally supports a `demo` mode that simulates deletion without hitting the server.
 *
 * @param {string} budgetId - The ID of the budget to update.
 * @param {boolean} [status=true] - `true` to mark for deletion, `false` to undo deletion.
 * @param {boolean} [demo=false] - If true, operates in local demo mode without server interaction.
 * @returns {Promise<boolean|void>} Resolves to `true` in demo mode, or nothing if server call succeeds.
 */
export const actUndoOrDeleteBudget = wrapWithLoading(
	async (budgetId, status = true, demo = false) => {
		if (demo) {
			runInAction(() => {
				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter((id) => id !== budgetId)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})

			return true
		}

		const plan_for_deletion = dayjs()
			.add(PLAN_FOR_DELETION_NR_OF_DAYS['budget'], 'day')
			.unix()
		const timestamp = status ? plan_for_deletion : false

		const _response = await deleteLstItem('budgets', {
			id: budgetId,
			to_delete: timestamp || false
		})
		const _storeItm = requireBudgetById(budgetId)
		_storeItm.setToDelete(timestamp)

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				_storeItm.setToDelete(timestamp || false)

				rootStore[_storeKey].setIds(
					rootStore[_storeKey].ids.filter((id) => id !== budgetId)
				)
				rootStore[_storeKey].setNr(rootStore[_storeKey].nr - 1)
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: 'Del/Undo Budget' }
)
